/**
 * Helpers offline terrain — détection workflow IV + overlay optimiste + réponses 202 enrichies.
 */

const OPTIMISTIC_STORAGE_KEY = 'ucp_terrain_iv_optimistic_v1';

export function parseInterventionIdFromWorkflowUrl(url = '') {
  const m = String(url || '').match(
    /\/intervenants\/interventions\/([^/]+)\/(accept|reject|start|complete|validate-report)\b/i
  );
  return m ? { id: m[1], action: m[2].toLowerCase() } : null;
}

export function isWorkflowMutationUrl(url = '') {
  return Boolean(parseInterventionIdFromWorkflowUrl(url));
}

export function isIntervenantReportMutationUrl(url = '') {
  const u = String(url || '');
  if (/generate-pdf/i.test(u)) return false;
  return /\/intervenant\/rapports(\/|$)/i.test(u) || /\/intervention-reports(\/|$)/i.test(u);
}

export function isDisponibiliteMutationUrl(url = '') {
  return /\/intervenant\/disponibilites(\/|$)/i.test(String(url || ''));
}

export function isClientReprogramMutationUrl(url = '') {
  return /\/client\/interventions\/[^/]+\/reprogrammer\b/i.test(String(url || ''));
}

export function isPlanningInterventionMutationUrl(url = '') {
  const u = String(url || '');
  return /\/interventions\/\d+\b/i.test(u) && !/generate-pdf/i.test(u);
}

export function buildOfflineQueuedResponseData(config, queueId) {
  const url = config?.url || '';
  const parsed = parseInterventionIdFromWorkflowUrl(url);
  const base = {
    success: true,
    queued: true,
    offline: true,
    queueId,
    message: 'Action enregistrée hors-ligne et synchronisée au retour réseau.',
  };

  if (!parsed) return base;

  const { id, action } = parsed;
  if (action === 'accept') {
    return { ...base, statut: 'planifiee', offlineOptimisticStatut: 'planifiee' };
  }
  if (action === 'reject') {
    return { ...base, statut: 'en_attente_reassignation', offlineOptimisticStatut: 'en_attente_reassignation' };
  }
  if (action === 'start') {
    return { ...base, statut: 'en_cours', offlineOptimisticStatut: 'en_cours' };
  }
  if (action === 'complete') {
    return {
      ...base,
      requiresReport: true,
      redirectTo: `/intervenant/rapports/nouveau?interventionId=${encodeURIComponent(id)}&offline=1`,
      offlineOptimisticStatut: 'en_cours',
    };
  }
  return base;
}

function readOptimisticMap() {
  try {
    const raw = localStorage.getItem(OPTIMISTIC_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeOptimisticMap(map) {
  try {
    localStorage.setItem(OPTIMISTIC_STORAGE_KEY, JSON.stringify(map));
  } catch {
    // quota — ignore
  }
}

export function setOptimisticInterventionStatus(interventionId, statut, meta = {}) {
  if (!interventionId || !statut) return;
  const map = readOptimisticMap();
  map[String(interventionId)] = {
    statut,
    updatedAt: Date.now(),
    ...meta,
  };
  writeOptimisticMap(map);
}

export function clearOptimisticInterventionStatus(interventionId) {
  if (!interventionId) return;
  const map = readOptimisticMap();
  delete map[String(interventionId)];
  writeOptimisticMap(map);
}

export function clearAllOptimisticInterventionStatuses() {
  try {
    localStorage.removeItem(OPTIMISTIC_STORAGE_KEY);
  } catch {
    // no-op
  }
}

export function getOptimisticInterventionStatus(interventionId) {
  const map = readOptimisticMap();
  return map[String(interventionId)] || null;
}

/** Applique overlay statut sur une liste d'interventions (lecture cache offline). */
export function applyOptimisticInterventionOverlay(list = []) {
  if (!Array.isArray(list) || list.length === 0) return list;
  const map = readOptimisticMap();
  return list.map((row) => {
    const patch = map[String(row?.id)];
    if (!patch?.statut) return row;
    return { ...row, statut: patch.statut, _offlineOptimistic: true };
  });
}

export function rememberOptimisticFromQueuedConfig(config) {
  const parsed = parseInterventionIdFromWorkflowUrl(config?.url || '');
  if (!parsed) return;
  const data = buildOfflineQueuedResponseData(config, null);
  if (data.offlineOptimisticStatut) {
    setOptimisticInterventionStatus(parsed.id, data.offlineOptimisticStatut, {
      action: parsed.action,
    });
  }
}

export function isFromOfflineQueue(response) {
  return Boolean(response?.fromOfflineQueue || response?.data?.queued || response?.data?.offline);
}
